import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../../components/ui/breadcrumb';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Loader2, AlertTriangle, Trash2, Database } from 'lucide-react';
import { toast } from 'sonner';
import ApiService from '../../services/api.service';
import { useTenantConfig } from '../../hooks/useTenantConfig';

type ResetType = 'semantic-layer' | 'everything' | 'archive' | null;

interface DeletedCounts {
  pipeline_nodes: number;
  relationships: number;
  measures: number;
  dimensions: number;
  analyses: number;
  dashboards: number;
  visualizations: number;
  notifications: number;
}

export default function DangerZone() {
  const queryClient = useQueryClient();
  const { data: tenantConfig } = useTenantConfig();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [resetType, setResetType] = useState<ResetType>(null);
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const expectedConfirmText = resetType === 'everything' ? 'DELETE EVERYTHING' : resetType === 'archive' ? 'DELETE TENANT' : 'DELETE';

  const handleOpenDialog = (type: ResetType) => {
    setResetType(type);
    setConfirmText('');
    setDialogOpen(true);
  };

  const handleClose = () => {
    if (!isDeleting) {
      setDialogOpen(false);
      setConfirmText('');
      setResetType(null);
    }
  };

  const handleConfirm = async () => {
    if (confirmText !== expectedConfirmText) return;

    setIsDeleting(true);
    try {
      if (resetType === 'archive') {
        if (!tenantConfig?.id) throw new Error('Tenant config not loaded');
        await ApiService.getInstance().request('POST', `/tenant-config/${tenantConfig.id}/archive`);
        toast.success('Tenant deleted');
        // Redirect — config will 404 and AuthenticatedRootPage will handle it
        queryClient.clear();
        window.location.href = '/';
        return;
      }

      const endpoint = resetType === 'everything'
        ? '/danger-zone/reset-everything'
        : '/danger-zone/reset-semantic-layer';

      const result = await ApiService.getInstance().request('POST', endpoint, { confirm: true }) as { ok: boolean; deleted_counts: DeletedCounts };

      if (result.ok) {
        const counts = result.deleted_counts;
        const message = `Deleted: ${counts.pipeline_nodes} nodes, ${counts.relationships} relationships, ${counts.measures} metrics, ${counts.dimensions} dimensions, ${counts.analyses} charts, ${counts.dashboards} dashboards`;
        toast.success('Reset complete', { description: message });

        // Clear all query caches
        queryClient.clear();
      }
    } catch (error: any) {
      console.error('Reset failed:', error);
      toast.error('Reset failed', { description: error?.message || 'An error occurred' });
    } finally {
      setIsDeleting(false);
      setDialogOpen(false);
      setConfirmText('');
      setResetType(null);
    }
  };

  return (
    <div className="w-full flex flex-col bg-background overflow-hidden" style={{ height: 'calc(100vh)' }}>
      {/* Top Bar */}
      <div className="flex items-center justify-between w-full px-6 py-4 border-b border-border bg-background flex-shrink-0 z-10">
        <div className="flex flex-col gap-2 flex-1 min-w-0">
          {/* Breadcrumbs */}
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/develop">Develop</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Danger Zone</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          {/* Page Title */}
          <h1 className="text-xl font-semibold text-foreground">Danger Zone</h1>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-8 min-h-0">
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Warning Banner */}
          <div className="flex items-start gap-3 p-4 rounded-lg bg-destructive/10 border border-destructive/20">
            <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
            <div className="text-sm text-destructive">
              <p className="font-semibold">Warning: These actions are irreversible</p>
              <p className="mt-1">
                The operations on this page will permanently delete data from your workspace.
                Make sure you understand what you're doing before proceeding.
              </p>
            </div>
          </div>

          {/* Reset Semantic Layer Card */}
          <Card className="border-destructive/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                Reset Semantic Layer
              </CardTitle>
              <CardDescription>
                Clear out all relationships, metrics, dimensions, charts, and dashboards,
                as well as all pipeline nodes that are not source tables. This lets you
                start with data modeling from scratch while keeping your source data connections.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="text-sm text-muted-foreground">
                  <p className="font-medium mb-2">This will delete:</p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>All relationships between nodes</li>
                    <li>All metrics (measures)</li>
                    <li>All dimensions</li>
                    <li>All charts (analyses)</li>
                    <li>All dashboards</li>
                    <li>All non-source pipeline nodes (entities, dimensions, reports, etc.)</li>
                  </ul>
                  <p className="mt-2 font-medium text-green-600 dark:text-green-400">
                    Source tables will be preserved.
                  </p>
                </div>
                <Button
                  variant="destructive"
                  onClick={() => handleOpenDialog('semantic-layer')}
                  className="w-full"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Reset Semantic Layer
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Reset Everything Card */}
          <Card className="border-destructive/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="h-5 w-5" />
                Reset Everything
              </CardTitle>
              <CardDescription>
                Clear out everything, including ALL pipeline nodes (including source tables).
                This is a complete reset of your entire data model.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="text-sm text-muted-foreground">
                  <p className="font-medium mb-2">This will delete:</p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>All relationships between nodes</li>
                    <li>All metrics (measures)</li>
                    <li>All dimensions</li>
                    <li>All charts (analyses)</li>
                    <li>All dashboards</li>
                    <li className="text-destructive font-medium">ALL pipeline nodes including source tables</li>
                  </ul>
                  <p className="mt-2 font-medium text-destructive">
                    This cannot be undone. You will need to re-connect all data sources.
                  </p>
                </div>
                <Button
                  variant="destructive"
                  onClick={() => handleOpenDialog('everything')}
                  className="w-full"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Reset Everything
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Delete Tenant Card */}
          <Card className="border-destructive/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" />
                Delete Tenant
              </CardTitle>
              <CardDescription>
                Delete this tenant. The tenant will be deactivated and users will no longer
                be able to access it. Data will no longer sync or be built.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                variant="destructive"
                onClick={() => handleOpenDialog('archive')}
                className="w-full"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Tenant
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={dialogOpen} onOpenChange={handleClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              {resetType === 'archive' ? 'Delete Tenant?' : resetType === 'everything' ? 'Reset Everything?' : 'Reset Semantic Layer?'}
            </DialogTitle>
            <DialogDescription>
              {resetType === 'archive'
                ? 'This will delete the tenant, cancel billing, and prevent all users from accessing it.'
                : resetType === 'everything'
                ? 'This will permanently delete ALL data including source tables. This action cannot be undone.'
                : 'This will permanently delete all your semantic layer data but keep source tables. This action cannot be undone.'
              }
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-2">
            <Label htmlFor="confirm-text">
              Type <span className="font-mono font-bold">{expectedConfirmText}</span> to confirm:
            </Label>
            <Input
              id="confirm-text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={expectedConfirmText}
              className="font-mono"
            />
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={handleClose} disabled={isDeleting}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirm}
              disabled={confirmText !== expectedConfirmText || isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Confirm Delete
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
